<template>
    <div class="grafico-svg">

        <!-- Estados explícitos: sem dado NUNCA vira zero nem 100%. -->
        <div v-if="semDados" class="grafico-vazio">
            {{ mensagemVazio }}
        </div>

        <svg v-else :viewBox="`0 0 ${largura} ${altura}`" preserveAspectRatio="xMidYMid meet"
             class="grafico-canvas" role="img" :aria-label="titulo">

            <!-- eixo Y de referência -->
            <line :x1="margemE" :y1="margemT" :x2="margemE" :y2="altura - margemB" class="eixo" />
            <line :x1="margemE" :y1="altura - margemB" :x2="largura - margemD" :y2="altura - margemB" class="eixo" />

            <!-- grade + rótulos do eixo Y -->
            <g v-for="(t, i) in ticks" :key="'t' + i">
                <line :x1="margemE" :y1="yDe(t)" :x2="largura - margemD" :y2="yDe(t)" class="grade" />
                <text :x="margemE - 6" :y="yDe(t) + 4" text-anchor="end" class="rotulo-eixo">{{ t }}</text>
            </g>

            <!-- linha de referência (frequência mínima, por exemplo) -->
            <g v-if="referencia !== null">
                <line :x1="margemE" :y1="yDe(referencia)" :x2="largura - margemD" :y2="yDe(referencia)"
                      class="referencia" />
                <text :x="largura - margemD" :y="yDe(referencia) - 5" text-anchor="end" class="rotulo-referencia">
                    {{ rotuloReferencia }}
                </text>
            </g>

            <!-- BARRAS -->
            <g v-if="tipo === 'barra'">
                <g v-for="(p, i) in pontosValidos" :key="'b' + i">
                    <rect :x="xDe(p.indice) - larguraBarra / 2" :y="yDe(p.valor)"
                          :width="larguraBarra" :height="Math.max(0, (altura - margemB) - yDe(p.valor))"
                          class="barra" />
                    <text :x="xDe(p.indice)" :y="yDe(p.valor) - 5" text-anchor="middle" class="valor">
                        {{ p.valor }}
                    </text>
                </g>
            </g>

            <!-- LINHAS (uma por série) -->
            <g v-else>
                <g v-for="(s, si) in seriesValidas" :key="'s' + si">
                    <polyline :points="pontosDaSerie(s)" class="linha" :style="{ stroke: cor(si) }" />
                    <circle v-for="(p, pi) in s.pontosValidos" :key="'c' + si + '-' + pi"
                            :cx="xDe(p.indice)" :cy="yDe(p.valor)" r="3.5" :style="{ fill: cor(si) }" />
                </g>
            </g>

            <!-- rótulos do eixo X -->
            <text v-for="(r, i) in rotulos" :key="'x' + i"
                  :x="xDe(i)" :y="altura - margemB + 16" text-anchor="middle" class="rotulo-eixo">
                {{ r }}
            </text>
        </svg>

        <!-- legenda das séries -->
        <div v-if="!semDados && tipo === 'linha' && seriesValidas.length > 1" class="legenda">
            <span v-for="(s, i) in seriesValidas" :key="'l' + i" class="legenda-item">
                <i class="legenda-cor" :style="{ background: cor(i) }"></i>{{ s.nome }}
            </span>
        </div>

        <!-- pontos que o backend marcou como indeterminados aparecem como lacuna declarada -->
        <div v-if="!semDados && temLacuna" class="grafico-nota">
            Períodos sem informação não são exibidos — ausência de dado não é zero.
        </div>
    </div>
</template>

<script>
/**
 * BLOCO N — item 14: gráfico do desempenho escolar.
 *
 * SVG puro, sem biblioteca: o portal não tinha stack de gráfico e o edital pede apenas
 * "em forma de gráfico". Evita adicionar dependência ao bundle do portal.
 *
 * REGRA: este componente NÃO calcula nada de acadêmico. Recebe os valores já calculados
 * pelo backend e apenas os desenha. Valor `null` é LACUNA — nunca é convertido em zero,
 * nunca vira 100%, e o ponto simplesmente não é plotado.
 */
export default {
    name: 'GraficoSvg',

    props: {
        // 'linha' | 'barra'
        tipo: { type: String, default: 'linha' },
        titulo: { type: String, default: 'Gráfico' },
        // rótulos do eixo X (ex.: ['1º Bim', '2º Bim'])
        rotulos: { type: Array, default: () => [] },
        // [{ nome, valores: [n|null, ...] }]
        series: { type: Array, default: () => [] },
        maximo: { type: Number, default: null },
        referencia: { type: Number, default: null },
        rotuloReferencia: { type: String, default: '' },
        mensagemVazio: { type: String, default: 'Ainda não há dados para exibir.' },
        altura: { type: Number, default: 240 }
    },

    data: () => ({
        largura: 520,
        margemE: 38, margemD: 14, margemT: 16, margemB: 26,
        cores: ['#409eff', '#67c23a', '#e6a23c', '#f56c6c', '#909399', '#7b5cd6']
    }),

    computed: {
        // Só entram no desenho os valores que o backend forneceu.
        seriesValidas () {
            return (this.series || []).map(s => {
                const pontos = (s.valores || []).map((v, i) => ({ indice: i, valor: v }))
                return Object.assign({}, s, {
                    pontosValidos: pontos.filter(p => p.valor !== null && p.valor !== undefined && p.valor !== '')
                })
            }).filter(s => s.pontosValidos.length > 0)
        },

        pontosValidos () {
            const s = this.seriesValidas[0]
            return s ? s.pontosValidos : []
        },

        semDados () {
            return this.seriesValidas.length === 0 || this.rotulos.length === 0
        },

        temLacuna () {
            return (this.series || []).some(s =>
                (s.valores || []).some(v => v === null || v === undefined || v === ''))
        },

        teto () {
            if (this.maximo !== null) return this.maximo
            let max = 0
            this.seriesValidas.forEach(s => s.pontosValidos.forEach(p => {
                if (Number(p.valor) > max) max = Number(p.valor)
            }))
            return max > 0 ? Math.ceil(max * 1.15) : 10
        },

        ticks () {
            const t = []
            const passo = this.teto / 4
            for (let i = 0; i <= 4; i++) t.push(Math.round(passo * i * 100) / 100)
            return t
        },

        larguraBarra () {
            const n = Math.max(1, this.rotulos.length)
            return Math.min(46, ((this.largura - this.margemE - this.margemD) / n) * 0.55)
        }
    },

    methods: {
        cor (i) { return this.cores[i % this.cores.length] },

        xDe (indice) {
            const n = Math.max(1, this.rotulos.length)
            const util = this.largura - this.margemE - this.margemD
            return this.margemE + (util / n) * (indice + 0.5)
        },

        yDe (valor) {
            const util = this.altura - this.margemT - this.margemB
            const v = Math.max(0, Math.min(Number(valor), this.teto))
            return this.margemT + util - (util * (v / (this.teto || 1)))
        },

        pontosDaSerie (s) {
            return s.pontosValidos.map(p => `${this.xDe(p.indice)},${this.yDe(p.valor)}`).join(' ')
        }
    }
}
</script>

<style scoped>
.grafico-svg { width: 100%; }
.grafico-canvas { width: 100%; height: auto; display: block; }
.eixo { stroke: #dcdfe6; stroke-width: 1; }
.grade { stroke: #f0f2f5; stroke-width: 1; }
.referencia { stroke: #e6a23c; stroke-width: 1.2; stroke-dasharray: 4 3; }
.rotulo-eixo { font-size: 9px; fill: #909399; }
.rotulo-referencia { font-size: 9px; fill: #e6a23c; }
.valor { font-size: 9px; fill: #606266; }
.barra { fill: #409eff; opacity: .85; }
.linha { fill: none; stroke-width: 2; }
.grafico-vazio { padding: 28px 12px; text-align: center; color: #909399; font-size: 13px;
                 background: #fafafa; border-radius: 4px; }
.grafico-nota { font-size: 11px; color: #909399; margin-top: 6px; }
.legenda { margin-top: 8px; font-size: 12px; color: #606266; }
.legenda-item { margin-right: 12px; display: inline-block; }
.legenda-cor { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 4px; }
</style>
